import { Helmet } from 'react-helmet-async';
import { DEFAULT_SHARE_IMAGE, jsonLdText, SITE_NAME, SITE_ORIGIN, SITE_SCHEMA } from '../lib/site';

/**
 * Per-page head tags for the public pages. Pre-rendered at build time
 * (scripts/prerender.mjs), so everything here must work without `window`.
 *
 * - `fullTitle`: used exactly as given (the website fix list's measured titles).
 * - `title`: gets " — MyDocUploads" added, unless it already ends with the name.
 * - `schemas`: extra JSON-LD objects; the site-wide Organization/WebSite block is always added.
 */
export default function Seo({
  title,
  fullTitle,
  description,
  canonicalPath,
  image,
  imageAlt,
  type = 'website',
  schemas = [],
}) {
  const pageTitle =
    fullTitle ||
    (title ? (/MyDocUploads\s*$/.test(title) ? title : `${title} — ${SITE_NAME}`) : SITE_NAME);
  const canonical = canonicalPath ? `${SITE_ORIGIN}${canonicalPath}` : undefined;
  const share = image
    ? { url: image.startsWith('http') ? image : `${SITE_ORIGIN}${image}`, alt: imageAlt }
    : DEFAULT_SHARE_IMAGE;
  return (
    <Helmet>
      <title>{pageTitle}</title>
      {description && <meta name="description" content={description} />}
      {canonical && <link rel="canonical" href={canonical} />}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      {description && <meta property="og:description" content={description} />}
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:image" content={share.url} />
      {share.width && <meta property="og:image:width" content={String(share.width)} />}
      {share.height && <meta property="og:image:height" content={String(share.height)} />}
      {share.alt && <meta property="og:image:alt" content={share.alt} />}
      <meta name="twitter:card" content="summary_large_image" />
      <script type="application/ld+json">{jsonLdText(SITE_SCHEMA)}</script>
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {jsonLdText(schema)}
        </script>
      ))}
    </Helmet>
  );
}
